import { useState, useEffect, useRef, CSSProperties } from 'react';

interface ImageLoaderProps {
  src: string;
  alt: string;
  className?: string;
  style?: CSSProperties;
  lazy?: boolean;
  loaderMaxHeight?: string;
}

const ImageLoader = ({ src, alt, className = '', style, lazy = true, loaderMaxHeight }: ImageLoaderProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isInView, setIsInView] = useState(!lazy);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset loading state when src changes
  useEffect(() => {
    setIsLoading(true);
  }, [src]);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!lazy) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [lazy]);

  const showLoader = isLoading || !isInView;

  return (
    <div ref={containerRef} className="relative w-full h-full">
      {/* Always render loader, toggle with opacity */}
      <div 
        className={`absolute left-0 right-0 top-0 flex items-center justify-center z-10 transition-opacity duration-300 rounded-t-2xl ${
          showLoader ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        style={{ 
          background: 'linear-gradient(135deg, hsl(220 25% 8%) 0%, hsl(220 20% 14%) 100%)',
          height: loaderMaxHeight || '100%',
          maxHeight: loaderMaxHeight || '100%'
        }}
      >
        <div className="relative">
          <img 
            src="/logo-white.png" 
            alt={alt || "Loading image"}
            className="h-8 w-auto animate-logo-pulse"
          />
          <div className="absolute inset-0 blur-lg opacity-30 animate-logo-glow" aria-hidden="true">
            <img src="/logo-white.png" alt="" role="presentation" className="h-8 w-auto" />
          </div>
        </div>
      </div>
      {isInView && (
        <img
          src={src}
          alt={alt}
          style={style}
          className={`${className} ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
          onLoad={() => setIsLoading(false)}
        />
      )}
    </div>
  );
};

export default ImageLoader;
